import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from '../lib/api.js';
import { Icon } from './Icon.js';

// Connect-a-number modal — CONNECT ONLY (pairing code first, QR fallback). Group picking lives in
// the persistent GroupSetupPanel on the Sessions page, so it can never be "lost" by closing a modal.
//   phone → 8-char pairing code (or QR) → connected → "המשך להגדרת קבוצות" (closes; panel takes over).
// `mode: 'new'` starts at the phone step; `mode: 'existing'` jumps straight to linking.

type Stage = 'phone' | 'linking';

interface Props {
  mode: 'new' | 'existing';
  accountId?: string;
  label?: string;
  onClose: () => void;
  /** Called once the number is connected — the Sessions page opens the group-setup panel for it. */
  onConnected?: (accountId: string) => void;
}

export function PairingModal({ mode, accountId: initialId, label, onClose, onConnected }: Props): JSX.Element {
  const qc = useQueryClient();
  const [stage, setStage] = useState<Stage>(mode === 'new' ? 'phone' : 'linking');
  const [phone, setPhone] = useState('');
  const [accountId, setAccountId] = useState<string | undefined>(initialId);

  const create = useMutation({
    mutationFn: () => api.createAccount(label ?? 'מספר חדש', phone.trim()),
    onSuccess: (r) => {
      setAccountId(r.id);
      setStage('linking');
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <h3>חיבור מספר{label ? ` · ${label}` : ''}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="סגור"><Icon name="close" /></button>
        </div>
        {stage === 'phone' && <PhoneStep phone={phone} setPhone={setPhone} onSubmit={() => create.mutate()} pending={create.isPending} error={create.isError} />}
        {stage === 'linking' && accountId && (
          <LinkingStep
            accountId={accountId}
            onDone={() => { qc.invalidateQueries({ queryKey: ['accounts'] }); onConnected?.(accountId); onClose(); }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Step 1: phone ──────────────────────────────────────────────────────────────────────────
function PhoneStep({ phone, setPhone, onSubmit, pending, error }: { phone: string; setPhone: (v: string) => void; onSubmit: () => void; pending: boolean; error: boolean }): JSX.Element {
  const valid = phone.replace(/\D/g, '').length >= 9;
  return (
    <div className="modal-body" style={{ gridTemplateColumns: '1fr' }}>
      <div>
        <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 14 }}>
          הזן את מספר ה-WhatsApp שברצונך לחבר (עם קידומת מדינה). נפיק לך <b>קוד קישור בן 8 תווים</b> שתקליד ישירות באפליקציה — ואם לא, נציג קוד QR לסריקה.
        </p>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>מספר טלפון</label>
        <input
          className="ltr"
          dir="ltr"
          inputMode="tel"
          placeholder="+972 54-000-0000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid) onSubmit(); }}
          style={{ width: '100%', height: 44, padding: '0 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 16, textAlign: 'left' }}
        />
        {error && <div style={{ color: 'var(--risk)', fontSize: 13, marginTop: 8 }}>לא הצלחנו ליצור את החיבור. בדוק את המספר ונסה שוב.</div>}
        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          <button className="btn" onClick={onSubmit} disabled={!valid || pending}>
            <Icon name="phone" /> {pending ? 'יוצר…' : 'הפק קוד קישור'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: linking (pairing code + QR fallback, polling status) ─────────────────────────────
function LinkingStep({ accountId, onDone }: { accountId: string; onDone: () => void }): JSX.Element {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['qr', accountId], queryFn: () => api.accountQr(accountId), refetchInterval: 2500 });

  useEffect(() => {
    if (data?.qr) QRCode.toDataURL(data.qr, { margin: 1, width: 200 }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
    else setQrDataUrl(null);
  }, [data?.qr]);

  const status = data?.status ?? 'connecting';
  const connected = status === 'connected';
  const code = data?.pairingCode ?? null;

  if (connected) {
    return (
      <div className="modal-body" style={{ gridTemplateColumns: '1fr' }}>
        <div style={{ textAlign: 'center', padding: '18px 0' }}>
          <div style={{ color: 'var(--ok)', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Icon name="check" /><div style={{ fontWeight: 700, fontSize: 18 }}>המספר מחובר!</div>
          </div>
          <p style={{ fontSize: 13.5, color: 'var(--text-dim)', margin: '12px auto 0', maxWidth: 380, lineHeight: 1.6 }}>
            עכשיו נבחר את הקבוצות לשליחה ואת קבוצת השליטה. אפשר לחזור להגדרות בכל רגע מכפתור <b>"קבוצות פרסום"</b> שבכרטיס המספר.
          </p>
          <button className="btn" style={{ marginTop: 18 }} onClick={onDone}>המשך להגדרת קבוצות</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-body" style={{ gridTemplateColumns: '1fr' }}>
      <div>
        {code ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: 6, fontSize: 13, color: 'var(--text-dim)' }}>קוד הקישור שלך</div>
            <div className="ltr" dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 40, fontWeight: 700, letterSpacing: 8, textAlign: 'center', padding: '14px 0', color: 'var(--accent-teal)' }}>
              {formatCode(code)}
            </div>
            <ol className="qr-steps" style={{ marginTop: 10, listStyle: 'none', padding: 0 }}>
              <li className="qr-step active"><span className="dot">1</span> פותחים WhatsApp בטלפון ← <b>מכשירים מקושרים</b></li>
              <li className="qr-step active"><span className="dot">2</span> מקישים <b>קישור מכשיר</b> ← <b>קשר עם מספר טלפון במקום</b></li>
              <li className="qr-step active"><span className="dot">3</span> מקלידים את הקוד שמופיע למעלה</li>
            </ol>
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>ממתין לחיבור… (הקוד מתחדש אוטומטית)</div>
            {qrDataUrl && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--accent-teal)', textAlign: 'center' }}>או סרוק קוד QR במקום</summary>
                <div className="qr-box" style={{ margin: '12px auto 0' }}><img src={qrDataUrl} alt="QR לחיבור" /></div>
              </details>
            )}
          </>
        ) : qrDataUrl ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 10 }}>סרוק את קוד ה-QR כדי לחבר את המספר</div>
            <div className="qr-box" style={{ margin: '0 auto' }}><img src={qrDataUrl} alt="QR לחיבור WhatsApp" /></div>
            <ol className="qr-steps" style={{ marginTop: 16, listStyle: 'none', padding: 0, textAlign: 'right' }}>
              <li className="qr-step active"><span className="dot">1</span> פותחים WhatsApp ← <b>מכשירים מקושרים</b></li>
              <li className="qr-step active"><span className="dot">2</span> מקישים <b>קישור מכשיר</b> וסורקים את הקוד</li>
            </ol>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-dim)' }}>
            <div className="skel" style={{ width: 140, height: 14, margin: '0 auto 10px' }} />
            מפיק קוד קישור / QR…
          </div>
        )}
      </div>
    </div>
  );
}

/** Group an 8-char code as "XXXX-XXXX" for readability (display only). */
function formatCode(code: string): string {
  const c = code.replace(/\s|-/g, '');
  return c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}
